import React, { Suspense, lazy, useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu } from 'lucide-react';
import MobileNavigation from './MobileNavigation';
import MobileBottomNav from './MobileBottomNav';
import DesktopNavigation from './DesktopNavigation';
import PwaInstallPrompt from '../PwaInstallPrompt';
import Logo from '../../assets/logo/Logo.png';
import LogoLight from '../../assets/logo/logo-dark.png';
import useAppTour from '../../hooks/useAppTour';
import useTheme from '../../hooks/useTheme';
import { useAppStore } from '../../store/useAppStore';
import { useOnboardingStore } from '../../store/useOnboardingStore';
import { useAppTourStore } from '../../store/useAppTourStore';
import { getRedirectUrl } from '../../utils/helpers';

const AppTour = lazy(() => import('../AppTour/AppTour'));

export const withNavigation = (WrappedComponent) => {
  return function NavigationLayout(props) {
    const { navigation = [], identity, onLogout, children, ...rest } = props;

    const location = useLocation();
    const navigate = useNavigate();
    const contactSupportUrl = getRedirectUrl('dashboard/support/contact');

    const { startTour } = useAppTour();
    const { completedTours } = useAppTourStore();
    const { shouldShowGuide, setShouldShowGuide, flowState } = useOnboardingStore();
    const { sidebarOpen, setSidebarOpen } = useAppStore();

    // Initialise theme on mount (reads localStorage / system preference)
    const { isDark } = useTheme();

    const handleStartTour = useCallback(() => {
      if (location.pathname !== '/dashboard') {
        navigate('/dashboard');
      }
      if (!sidebarOpen) {
        setSidebarOpen(true);
      }
      startTour();
       
    }, [sidebarOpen, setSidebarOpen, startTour, location.pathname]);

    const isCurrentPath = useCallback(
      (path) => location.pathname === path || location.pathname.startsWith(`${path}/`),
      [location.pathname]
    );
    const isEditorRoute = ['/editor', '/html-editor'].some(
      (p) => location.pathname === p || location.pathname.startsWith(`${p}/`)
    );

    const activeNavigationItem = useMemo(
      () => navigation.find((item) => isCurrentPath(item.href)),
      [navigation, isCurrentPath]
    );

    const mobileTabNavigation = useMemo(
      () => navigation.filter((item) => item.showInMobileTabs),
      [navigation]
    );

    const currentMobileTitle = activeNavigationItem?.label || activeNavigationItem?.name || 'Home';

    useEffect(() => {
      if (!shouldShowGuide) return;
      const hasTourBeenCompleted = completedTours.includes('default');
      if (shouldShowGuide && flowState === 'completed' && !hasTourBeenCompleted) {
        if (!sidebarOpen) setSidebarOpen(true);
        startTour();
        setShouldShowGuide(false);
      }
       
    }, [shouldShowGuide, flowState, startTour, sidebarOpen, completedTours]);

    if (location.pathname === '/test-page') {
      return <>{children}</>;
    }

    return (
      <div className='app-shell'>
        <Suspense fallback={null}>
          <AppTour />
        </Suspense>

        <MobileNavigation
          navigation={navigation}
          isCurrentPath={isCurrentPath}
          identity={identity}
          onLogout={onLogout}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          onStartTour={handleStartTour}
          contactSupportUrl={contactSupportUrl}
        />

        <DesktopNavigation
          navigation={navigation}
          isCurrentPath={isCurrentPath}
          identity={identity}
          onLogout={onLogout}
          onStartTour={handleStartTour}
          contactSupportUrl={contactSupportUrl}
        />

        {/* Main content area */}
        <div className='flex flex-col h-screen transition-all duration-300 lg:pl-64'>
          {/* Mobile top-bar */}
          <div
            className='mobile-topbar sticky top-0 z-20 lg:hidden'
            style={{
              borderBottom: '1px solid var(--sidebar-border)',
            }}
          >
            <div className='flex items-center justify-between gap-4 px-4 pb-3'>
              <div className='min-w-0'>
                <div className='mb-1 flex items-center gap-2'>
                  <img src={isDark ? Logo : LogoLight} alt='Postreams' className='h-6 w-auto shrink-0' />
                </div>
                <h1 className='truncate text-lg font-semibold' style={{ color: 'var(--text-primary)' }}>
                  {currentMobileTitle}
                </h1>
              </div>

              <button
                type='button'
                aria-label='Open menu'
                onClick={() => setSidebarOpen(true)}
                className='flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl cursor-pointer transition-colors'
                style={{
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border)',
                  color: 'var(--text-primary)',
                  boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
                }}
              >
                <Menu style={{ width: '1.2rem', height: '1.2rem' }} />
              </button>
            </div>
          </div>

          {/* Page content */}
          <main
            className={`flex-grow ${isEditorRoute ? 'overflow-hidden' : 'overflow-y-auto thin-scrollbar'}`}
            style={{ background: 'var(--bg-base)' }}
          >
            <div
              className={
                isEditorRoute
                  ? 'h-full w-full'
                  : 'mobile-page-frame container mx-auto max-w-7xl px-4 py-5 sm:px-6 sm:py-6 lg:px-8'
              }
              data-tour='main-content'
            >
              <WrappedComponent {...rest}>{children}</WrappedComponent>
            </div>
          </main>

          <PwaInstallPrompt />
          <MobileBottomNav navigation={mobileTabNavigation} isCurrentPath={isCurrentPath} />
        </div>
      </div>
    );
  };
};

export default withNavigation;
